// Change C — data-driven confidence (plan §1.1 / §4.4 / doc 42 §3).
//
// PURE, ADDITIVE module. Nothing here runs for a manifest that lacks a
// `confidence` string AND a `confidenceRaster` block — which is the case for all
// three existing regions (EU/NA/Asia) — so it cannot regress them.
//
// CANONICAL REPRESENTATION (binding, plan §1.1): a region's tier is a PLAIN
// TOP-LEVEL STRING `manifest.confidence` in the 4-value enum
//   "measured" | "literature" | "model" | "synthetic"
// The object form `{ confidence: { tier: ... } }` is FORBIDDEN project-wide and
// has NO branch here. parseConfidence validates against the enum and QA-FAILS
// (logs + flags) on an unrecognized non-empty value — it NEVER silently returns
// undefined for a present-but-wrong value. Absent => undefined => NO overlay.

import type { Manifest } from "./manifest";

/** The 4 visible confidence tiers, best -> worst. */
export type ConfidenceTier = "measured" | "literature" | "model" | "synthetic";

/** Display metadata per tier (label + badge color + best->worst order). */
export interface ConfidenceMeta {
  label: string;
  /** Badge accent color (#RRGGBB). */
  color: string;
  /** Display order: 0 = best (measured) .. 3 = worst (synthetic). */
  order: number;
}

export const CONFIDENCE_META: Readonly<Record<ConfidenceTier, ConfidenceMeta>> =
  {
    measured: { label: "Measured", color: "#2e9e5b", order: 0 },
    literature: { label: "Literature", color: "#3b82c4", order: 1 },
    model: { label: "Model", color: "#d08a1e", order: 2 },
    synthetic: { label: "Synthetic", color: "#b04a4a", order: 3 },
  };

/** The fixed enum set (single source of truth for validation). */
const TIER_SET: ReadonlySet<string> = new Set<ConfidenceTier>([
  "measured",
  "literature",
  "model",
  "synthetic",
]);

/** Type guard: is `s` exactly one of the 4 enum tiers? */
export function isConfidenceTier(s: unknown): s is ConfidenceTier {
  return typeof s === "string" && TIER_SET.has(s);
}

/**
 * Map the research 5-rung ladder (1..5) to the 4 visible tiers (plan §1.1):
 *   rung 1 -> measured; rung 2 -> model; rung 3 -> literature;
 *   rung 4 -> model;    rung 5 -> synthetic.
 * (Used only where a numeric rung is the source; the manifest tier itself is a
 * string, never a number, so this is a helper for QA / provenance tooling.)
 */
export function rungToTier(rung: number): ConfidenceTier | undefined {
  switch (Math.round(rung)) {
    case 1:
      return "measured";
    case 2:
      return "model";
    case 3:
      return "literature";
    case 4:
      return "model";
    case 5:
      return "synthetic";
    default:
      return undefined;
  }
}

/** Result of parsing a region's tier (carries a QA-fail flag, never silent). */
export interface ParsedConfidence {
  tier: ConfidenceTier | undefined;
  /** True when a present `confidence` value was NOT a recognized enum string. */
  qaFail: boolean;
  reason?: string;
}

/**
 * Parse a region's confidence tier from its manifest. Reads ONLY the top-level
 * `manifest.confidence` STRING (the object form is unsupported by design).
 *   - absent / null      => { tier: undefined, qaFail: false }  (NO overlay)
 *   - valid enum string  => { tier, qaFail: false }
 *   - any other value    => { tier: undefined, qaFail: true }   (LOUD, not silent)
 *
 * The QA-fail path logs a warning so a mistyped data package is caught at
 * integration, never silently downgraded to no-badge.
 */
export function parseConfidence(manifest: Manifest): ParsedConfidence {
  const c = manifest.confidence;
  if (c === undefined || c === null) return { tier: undefined, qaFail: false };
  if (typeof c !== "string") {
    const reason = `confidence is not a string: ${JSON.stringify(c)} (the object form is forbidden)`;
    // eslint-disable-next-line no-console
    console.warn(`[confidence] QA-FAIL — ${reason}`);
    return { tier: undefined, qaFail: true, reason };
  }
  if (!isConfidenceTier(c)) {
    const reason = `confidence "${c}" is not one of {measured,literature,model,synthetic}`;
    // eslint-disable-next-line no-console
    console.warn(`[confidence] QA-FAIL — ${reason}`);
    return { tier: undefined, qaFail: true, reason };
  }
  return { tier: c, qaFail: false };
}

/** Convenience: just the tier (undefined when absent OR QA-fail). */
export function confidenceTierOf(manifest: Manifest): ConfidenceTier | undefined {
  return parseConfidence(manifest).tier;
}

// --- Per-pixel CONF raster (plan §1.1-D) ------------------------------------
//
// NEW additive plumbing. The byte->level decode FORMULA is REUSED (identical to
// pollenLayer.ts:88 `floor(raw*255/64+0.5)` and the pollen-strip CPU decode),
// but the FETCH + ImageBitmap/canvas decode below is genuinely new code (audit
// 2.1 — reusing the formula is not reusing the decode path). It runs ONLY when a
// region ships a `confidenceRaster` block; every existing region ships none, so
// this never executes for them.

/**
 * Decode a raw 0..255 byte to its ordinal level {0,1,2,3,4} (the FORMULA).
 * This is the CPU equivalent of the shader's `floor(raw*255/64+0.5)`
 * (pollenLayer.ts:88) where `raw = byte/255`, so it reduces to `floor(byte/64+0.5)`
 * — identical levels for the {0,64,128,192,255} ladder. Reused, not reinvented.
 */
export function byteToLevel(byte: number): number {
  return Math.floor(byte / 64 + 0.5);
}

/** Default byte->tier ladder (ascending confidence) when none is declared. */
const DEFAULT_BYTE_TO_TIER: Readonly<Record<number, ConfidenceTier>> = {
  64: "synthetic",
  128: "model",
  192: "literature",
  255: "measured",
};

/** A decoded per-pixel CONF raster: width*height ordinal tier-levels (0=no data). */
export interface ConfidenceRasterData {
  width: number;
  height: number;
  /** length width*height*nSlices; value = tier level 0..4 (0 = NO_DATA). */
  levels: Uint8Array;
  nSlices: number;
}

/**
 * Resolve the manifest's byteToTier table into a level(1..4) -> tier lookup.
 * Uses the declared `byteToTier` when present, else the default ladder.
 */
export function tierForLevel(
  raster: { byteToTier?: Record<string, string> } | undefined,
): (level: number) => ConfidenceTier | undefined {
  const map = new Map<number, ConfidenceTier>();
  const src = raster?.byteToTier;
  if (src) {
    for (const [byteStr, tier] of Object.entries(src)) {
      const byte = Number.parseInt(byteStr, 10);
      if (Number.isFinite(byte) && isConfidenceTier(tier)) {
        map.set(byteToLevel(byte), tier);
      }
    }
  } else {
    for (const [byteStr, tier] of Object.entries(DEFAULT_BYTE_TO_TIER)) {
      map.set(byteToLevel(Number.parseInt(byteStr, 10)), tier);
    }
  }
  return (level: number) => map.get(level);
}

/**
 * Fetch + decode a region's per-pixel CONF raster into ordinal tier levels.
 * NEW code; INACTIVE unless `manifest.confidenceRaster` is present. Returns
 * undefined (no throw) when there is no raster to fetch, so callers can guard
 * cheaply. Reuses the same byte->level FORMULA as the pollen-strip decode.
 *
 * Layout matches the pollen strips: a static raster is one (w x h) image; a
 * week-varying raster is the 4x13 strip layout (slice j of strip s at rows
 * [j*h .. j*h+h-1], global index s*slicesPerStrip + j).
 */
export async function loadConfidenceRaster(
  manifest: Manifest,
  base: string,
  width: number,
  height: number,
  nSlices: number,
  slicesPerStrip: number,
): Promise<ConfidenceRasterData | undefined> {
  const cr = manifest.confidenceRaster;
  if (!cr) return undefined; // no raster -> inactive (existing-region path)

  const decodeStrip = async (
    url: string,
    stripIndex: number,
    out: Uint8Array,
  ): Promise<void> => {
    const resp = await fetch(url);
    if (!resp.ok) throw new Error(`${url} -> HTTP ${resp.status}`);
    const blob = await resp.blob();
    const canvas =
      typeof OffscreenCanvas !== "undefined"
        ? new OffscreenCanvas(width, height)
        : Object.assign(document.createElement("canvas"), { width, height });
    const ctx = (canvas as HTMLCanvasElement | OffscreenCanvas).getContext(
      "2d",
      { willReadFrequently: true },
    ) as CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;
    for (let j = 0; j < slicesPerStrip; j++) {
      const globalSlice = stripIndex * slicesPerStrip + j;
      if (globalSlice >= nSlices) break;
      const bitmap = await createImageBitmap(blob, 0, j * height, width, height, {
        colorSpaceConversion: "none",
        premultiplyAlpha: "none",
      });
      ctx.clearRect(0, 0, width, height);
      ctx.drawImage(bitmap, 0, 0);
      bitmap.close();
      const img = ctx.getImageData(0, 0, width, height).data; // RGBA
      const off = globalSlice * width * height;
      for (let p = 0; p < width * height; p++) {
        out[off + p] = byteToLevel(img[p * 4]); // RED channel -> level
      }
    }
  };

  const levels = new Uint8Array(width * height * nSlices);
  if (cr.file) {
    // Static (52-invariant) raster: decode the single image into slice 0, then
    // broadcast across all slices.
    const single = new Uint8Array(width * height);
    const resp = await fetch(`${base}${cr.file}`);
    if (!resp.ok) throw new Error(`${base}${cr.file} -> HTTP ${resp.status}`);
    const blob = await resp.blob();
    const canvas =
      typeof OffscreenCanvas !== "undefined"
        ? new OffscreenCanvas(width, height)
        : Object.assign(document.createElement("canvas"), { width, height });
    const ctx = (canvas as HTMLCanvasElement | OffscreenCanvas).getContext(
      "2d",
      { willReadFrequently: true },
    ) as CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;
    const bitmap = await createImageBitmap(blob, {
      colorSpaceConversion: "none",
      premultiplyAlpha: "none",
    });
    ctx.drawImage(bitmap, 0, 0);
    bitmap.close();
    const img = ctx.getImageData(0, 0, width, height).data;
    for (let p = 0; p < width * height; p++) single[p] = byteToLevel(img[p * 4]);
    for (let s = 0; s < nSlices; s++) levels.set(single, s * width * height);
  } else if (cr.files && cr.files.length > 0) {
    for (let s = 0; s < cr.files.length; s++) {
      await decodeStrip(`${base}${cr.files[s]}`, s, levels);
    }
  } else {
    return undefined; // malformed block -> treat as absent (no overlay)
  }

  return { width, height, levels, nSlices };
}
